import { createContext, useCallback, useContext, useRef, useState, type ReactNode } from "react"
import { Button } from "./Button.js"

type ToastVariant = "info" | "success" | "error"

interface BaseToast {
  id: string
  message: string
  variant: ToastVariant
}

interface UndoableToast extends BaseToast {
  kind: "undoable"
  onCommit: () => void
  onUndo?: () => void
}

interface PlainToast extends BaseToast {
  kind: "plain"
}

type ToastItem = UndoableToast | PlainToast

interface ToastContextValue {
  /** Simple, non-destructive feedback: "Guardado", "No se pudo conectar", etc. */
  show: (message: string, variant?: ToastVariant) => void
  /**
   * The undo pattern for destructive actions (design principle #3): the
   * action is NOT executed immediately. It's scheduled after
   * `--undo-delay` (3000ms, see tokens.css) and the toast shows a
   * draining progress bar + a "Deshacer" button. Only calls `onCommit`
   * if the window elapses without the user undoing.
   */
  confirmDestructive: (message: string, onCommit: () => void, onUndo?: () => void) => void
}

const ToastContext = createContext<ToastContextValue | null>(null)

const UNDO_DELAY_MS = 3000 // mirrors --undo-delay in tokens.css

const VARIANT_COLOR: Record<ToastVariant, string> = {
  info: "var(--status-info)",
  success: "var(--status-success)",
  error: "var(--status-error)",
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([])
  const timers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map())

  const dismiss = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id))
    const timer = timers.current.get(id)
    if (timer) {
      clearTimeout(timer)
      timers.current.delete(id)
    }
  }, [])

  const show = useCallback(
    (message: string, variant: ToastVariant = "info") => {
      const id = crypto.randomUUID()
      setToasts((prev) => [...prev, { id, message, variant, kind: "plain" }])
      const timer = setTimeout(() => dismiss(id), 4000)
      timers.current.set(id, timer)
    },
    [dismiss],
  )

  const confirmDestructive = useCallback(
    (message: string, onCommit: () => void, onUndo?: () => void) => {
      const id = crypto.randomUUID()
      setToasts((prev) => [...prev, { id, message, variant: "info", kind: "undoable", onCommit, onUndo }])
      const timer = setTimeout(() => {
        onCommit()
        dismiss(id)
      }, UNDO_DELAY_MS)
      timers.current.set(id, timer)
    },
    [dismiss],
  )

  return (
    <ToastContext.Provider value={{ show, confirmDestructive }}>
      {children}
      <div
        aria-live="polite"
        style={{
          position: "fixed",
          bottom: "var(--space-6)",
          right: "var(--space-6)",
          display: "flex",
          flexDirection: "column",
          gap: "var(--space-3)",
          zIndex: 1000,
          maxWidth: 360,
        }}
      >
        {toasts.map((toast) => (
          <ToastCard
            key={toast.id}
            toast={toast}
            onUndo={() => {
              if (toast.kind === "undoable") toast.onUndo?.()
              dismiss(toast.id)
            }}
            onDismiss={() => dismiss(toast.id)}
          />
        ))}
      </div>
    </ToastContext.Provider>
  )
}

function ToastCard({
  toast,
  onUndo,
  onDismiss,
}: {
  toast: ToastItem
  onUndo: () => void
  onDismiss: () => void
}) {
  return (
    <div
      role="status"
      style={{
        background: "var(--bg-surface-raised)",
        border: "1px solid var(--border-default)",
        borderRadius: "var(--radius-md)",
        boxShadow: "var(--shadow-lg)",
        padding: "var(--space-4)",
        display: "flex",
        flexDirection: "column",
        gap: "var(--space-3)",
        overflow: "hidden",
        position: "relative",
        animation: "mc-toast-in var(--duration-medium) var(--ease-out)",
      }}
    >
      <div style={{ display: "flex", alignItems: "flex-start", gap: "var(--space-3)" }}>
        <span
          style={{
            width: 8,
            height: 8,
            marginTop: 6,
            flexShrink: 0,
            borderRadius: "var(--radius-full)",
            background: VARIANT_COLOR[toast.variant],
          }}
        />
        <p style={{ fontSize: "var(--text-sm)", flex: 1 }}>{toast.message}</p>
        {toast.kind === "undoable" && (
          <Button variant="ghost" size="sm" onClick={onUndo}>
            Deshacer
          </Button>
        )}
        {toast.kind === "plain" && (
          <button
            aria-label="Cerrar"
            onClick={onDismiss}
            style={{ background: "none", border: "none", color: "var(--text-muted)" }}
          >
            ✕
          </button>
        )}
      </div>
      {toast.kind === "undoable" && (
        <div style={{ height: 3, background: "var(--border-default)", borderRadius: "var(--radius-full)" }}>
          <div
            style={{
              height: "100%",
              background: VARIANT_COLOR.error,
              borderRadius: "var(--radius-full)",
              animation: "mc-toast-drain var(--undo-delay) linear forwards",
            }}
          />
        </div>
      )}
      <style>{`
        @keyframes mc-toast-in {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes mc-toast-drain {
          from { width: 100%; }
          to { width: 0%; }
        }
      `}</style>
    </div>
  )
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext)
  if (!ctx) throw new Error("useToast must be used within a ToastProvider")
  return ctx
}
